/**
 * T131 — Expense categories API client + hooks.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { expenseCategoryKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';
import type {
  CreateExpenseCategoryInput,
  UpdateExpenseCategoryInput,
  ExpenseCategoryDto,
} from '@armard/shared';

interface PaginatedCategories {
  items: ExpenseCategoryDto[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface CategoryListQueryInput {
  page?: number;
  limit?: number;
  q?: string;
  accountingClass?: 'sammelposten' | 'abschreibung';
  includeArchived?: boolean;
}

function buildQuery(q: CategoryListQueryInput): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const categoriesApi = {
  list: (q: CategoryListQueryInput = {}) =>
    api.get<PaginatedCategories>(`/expense-categories${buildQuery(q)}`),
  get: (id: string) => api.get<ExpenseCategoryDto>(`/expense-categories/${id}`),
  create: (input: CreateExpenseCategoryInput) =>
    api.post<ExpenseCategoryDto>('/expense-categories', input),
  update: (id: string, input: UpdateExpenseCategoryInput) =>
    api.put<ExpenseCategoryDto>(`/expense-categories/${id}`, input),
  // T-G6-soft-delete — returns 409 with `error: 'in_use'` or
  // `'system_default'` when applicable; callers (Settings page) read
  // the response body to decide whether to offer "archive instead".
  delete: (id: string) => api.delete<{ deleted: true }>(`/expense-categories/${id}`),
};

export function useCategories(query: CategoryListQueryInput = {}) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseCategoryKeys.list(tenantId, query),
    queryFn: () => categoriesApi.list(query),
    enabled: tenantId !== null,
  });
}

export function useCategory(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseCategoryKeys.detail(tenantId, id!),
    queryFn: () => categoriesApi.get(id!),
    enabled: !!id && tenantId !== null,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: CreateExpenseCategoryInput) => categoriesApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseCategoryKeys.all(tenantId) }),
  });
}

export function useUpdateCategory(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: UpdateExpenseCategoryInput) => categoriesApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseCategoryKeys.all(tenantId) }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseCategoryKeys.all(tenantId) }),
  });
}
